import queryString from 'query-string';

import { Admin, AdminInvite, BillingAddress } from 'api/types/shared.types';
import { Volop } from 'api/volopsAdmin';
import { BackgroundTask } from 'utils/asyncBackendTask';
import { LISTING_TYPE_URL_SEGMENT_MAP } from 'utils/constants';
import { filterNulls } from 'utils/functional';
import { del, getJson, patchJson, postJson, putJson } from 'utils/http';
import { titleCaseToCamelCase } from 'utils/string';

import { ApiOrgAdminCandidate } from './types/ApiOrgAdminCandidate';
import { ApiOrgAdminContact } from './types/ApiOrgAdminContact';
import { ApiOrgAdminCredit } from './types/ApiOrgAdminCredit';
import { ApiOrgAdminCreditPack } from './types/ApiOrgAdminCreditPack';
import { ApiOrgAdminEmailNotificationPreferencesInfo } from './types/ApiOrgAdminEmailNotificationPreferencesInfo';
import { ApiOrgAdminGradProgram } from './types/ApiOrgAdminGradProgram';
import { ApiOrgAdminIntegrationJob } from './types/ApiOrgAdminIntegrationJob';
import { ApiOrgAdminIntegrationProvider } from './types/ApiOrgAdminIntegrationProvider';
import { ApiOrgAdminInternship } from './types/ApiOrgAdminInternship';
import { ApiOrgAdminInvoice } from './types/ApiOrgAdminInvoice';
import { ApiOrgAdminJob } from './types/ApiOrgAdminJob';
import { ApiOrgAdminLatestAtsUsage } from './types/ApiOrgAdminLatestAtsUsage';
import { ApiOrgAdminOrg } from './types/ApiOrgAdminOrg';
import { ApiOrgAdminOrgNotificationPreferences } from './types/ApiOrgAdminOrgNotificationPreferences';
import { ApiOrgAdminPayInvoiceByCreditCardResult } from './types/ApiOrgAdminPayInvoiceByCreditCardResult';
import { ApiOrgAdminPayment } from './types/ApiOrgAdminPayment';
import { ApiOrgAdminRepostedListing } from './types/ApiOrgAdminRepostedListing';
import { OrgSize } from './types/OrgSize';
import { PreferredListingType } from './types/PreferredListingType';

export function fetchOrg(orgId: string): Promise<{
  org: ApiOrgAdminOrg;
  user: { preferredListingTypes: PreferredListingType[]; orgSize: OrgSize };
}> {
  return getJson(`/data/orgdashboard/${orgId}/organization`);
}

export function updateOrg(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  orgInfo: Record<string, any>
): Promise<ApiOrgAdminOrg> {
  return putJson(`/data/orgdashboard/${orgInfo.id}/organization`, orgInfo).then(
    // @ts-expect-error TS(2345): Argument of type '({ org }: { org: any; }) => any'... Remove this comment to see the full error message
    ({ org }) => org
  );
}

export function partialUpdateOrg(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  orgInfo: Record<string, any>
): Promise<ApiOrgAdminOrg> {
  return patchJson(
    `/data/orgdashboard/${orgInfo.id}/organization`,
    orgInfo
  ).then(
    // @ts-expect-error TS(2345): Argument of type '({ org }: { org: any; }) => any'... Remove this comment to see the full error message
    ({ org }) => org
  );
}

export function deleteOrg(orgId: string) {
  return del(`/data/orgdashboard/${orgId}/organization`);
}

export function addOwner(
  orgId: string,
  email: string
): Promise<{
  user: Admin | null | undefined;
  invite: AdminInvite | null | undefined;
}> {
  return postJson(`/data/orgdashboard/${orgId}/owners`, {
    email,
  });
}

export function addAdmin(
  orgId: string,
  email: string
): Promise<{
  user: Admin | null | undefined;
  invite: AdminInvite | null | undefined;
}> {
  return postJson(`/data/orgdashboard/${orgId}/admins`, {
    email,
  });
}

export function removeAdmin(orgId: string, userId: string) {
  return del(`/data/orgdashboard/${orgId}/admins/${userId}`);
}

export function approveAdminRequest(orgId: string, requestId: string) {
  return postJson(`/data/orgdashboard/${orgId}/admin-requests/${requestId}`, {
    approved: true,
    // @ts-expect-error TS(2345): Argument of type '({ admin }: { admin: any; }) => ... Remove this comment to see the full error message
  }).then(({ admin }) => admin);
}

export function rejectAdminRequest(orgId: string, requestId: string) {
  return postJson(`/data/orgdashboard/${orgId}/admin-requests/${requestId}`, {
    approved: false,
  });
}

export function updateVatNumber(orgId: string, vatNumber: string) {
  return putJson(`/data/orgdashboard/${orgId}/organization/vat`, {
    vatNumber,
    // @ts-expect-error TS(2345): Argument of type '({ org }: { org: any; }) => any'... Remove this comment to see the full error message
  }).then(({ org }) => org);
}

export function deleteBillingAddress(orgId: string, addressId: string) {
  return del(`/data/orgdashboard/${orgId}/billing-addresses/${addressId}`);
}

export function createVolop(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  volop: Volop;
}> {
  return postJson(`/data/orgdashboard/${orgId}/volops`, payload);
}

export function validateVolop(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  volop: Volop;
}> {
  return postJson(`/data/orgdashboard/${orgId}/volops-validation`, payload);
}

export function updateVolop(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<{
  volop: Volop;
}> {
  return putJson(`/data/orgdashboard/${orgId}/volops/${payload.id}`, payload);
}

export function fetchListing(
  orgId: string,
  listingType: string,
  listingId: string
): Promise<ApiOrgAdminJob | ApiOrgAdminInternship | Volop> {
  return getJson(
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    `/data/orgdashboard/${orgId}/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}`
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
  ).then((response) => response[titleCaseToCamelCase(listingType)]);
}

export function hideListing({
  orgId,
  listingId,
  listingType,
}: {
  orgId: string;
  listingId: string;
  listingType: string;
}) {
  return postJson(
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    `/data/orgdashboard/${orgId}/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}/hide`
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
  ).then((response) => response[titleCaseToCamelCase(listingType)]);
}

export function unhideListing({
  orgId,
  listingId,
  listingType,
}: {
  orgId: string;
  listingId: string;
  listingType: string;
}) {
  return postJson(
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    `/data/orgdashboard/${orgId}/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}/unhide`
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
  ).then((response) => response[titleCaseToCamelCase(listingType)]);
}

export function deleteListing({
  orgId,
  listingId,
  listingType,
}: {
  orgId: string;
  listingId: string;
  listingType: string;
}) {
  return del(
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    `/data/orgdashboard/${orgId}/${LISTING_TYPE_URL_SEGMENT_MAP[listingType]}/${listingId}`
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
  ).then((response) => response[titleCaseToCamelCase(listingType)]);
}

export function fetchRepostedListings(
  orgId: string
): Promise<Array<ApiOrgAdminRepostedListing>> {
  return getJson(`/data/orgdashboard/${orgId}/published-reposts`).then(
    // @ts-expect-error TS(2345): Argument of type '({ listings }: { listings: any; ... Remove this comment to see the full error message
    ({ listings }) => listings
  );
}

export function fetchInvoices(
  orgId: string,
  filters: {
    number: string | null | undefined;
    before: string | null | undefined;
    after: string | null | undefined;
    outstandingOnly?: boolean;
    pageIndex: number;
    perPage: number;
  }
): Promise<{
  invoices: Array<ApiOrgAdminInvoice>;
  count: number;
  perPage: number;
  nbPages: number;
}> {
  const { number, before, after, outstandingOnly, pageIndex, perPage } =
    filters;
  const params = {
    number,
    before,
    after,
    outstanding: Boolean(outstandingOnly),
    page: pageIndex,
    perPage,
  };
  return getJson(
    `/data/orgdashboard/${orgId}/invoices?${queryString.stringify(params)}`
  );
}

export function fetchOpenInvoices(
  orgId: string
): Promise<Array<ApiOrgAdminInvoice>> {
  return getJson(`/data/orgdashboard/${orgId}/open-invoices`).then(
    // @ts-expect-error TS(2345): Argument of type '({ invoices }: { invoices: any; ... Remove this comment to see the full error message
    ({ invoices }) => invoices
  );
}

export function fetchInvoice(
  orgId: string,
  invoiceId: string
): Promise<ApiOrgAdminInvoice> {
  return getJson(`/data/orgdashboard/${orgId}/invoices/${invoiceId}`).then(
    // @ts-expect-error TS(2345): Argument of type '({ invoice }: { invoice: any; })... Remove this comment to see the full error message
    ({ invoice }) => invoice
  );
}

export function updateInvoiceOrgNote(
  orgId: string,
  invoiceId: string,
  orgNote: string
): Promise<void> {
  return putJson(`/data/orgdashboard/${orgId}/invoices/${invoiceId}/org-note`, {
    orgNote,
  });
}

export function updateInvoiceBillingAddress(
  orgId: string,
  invoiceId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<void> {
  return putJson(
    `/data/orgdashboard/${orgId}/invoices/${invoiceId}/billing-address`,
    payload
  );
}

export function payInvoiceByCheck(
  orgId: string,
  invoiceId: string
): Promise<ApiOrgAdminInvoice> {
  return postJson(`/data/orgdashboard/${orgId}/pay-by-check`, {
    invoiceIds: [invoiceId],
  });
}

export function payInvoicesByCreditCard(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<BackgroundTask<ApiOrgAdminPayInvoiceByCreditCardResult>> {
  return postJson(
    `/data/orgdashboard/${orgId}/pay-by-credit-card`,
    payload
    // @ts-expect-error TS(2345): Argument of type '({ task }: { task: any; }) => an... Remove this comment to see the full error message
  ).then(({ task }) => task);
}

export function payLineItemsWithCredits(
  orgId: string,
  lineItemIds: Array<string>
): Promise<ApiOrgAdminInvoice> {
  return postJson(`/data/orgdashboard/${orgId}/use-credits`, {
    lineItemIds,
  });
}

export function removeLineItem(
  orgId: string,
  lineItemId: string
): Promise<ApiOrgAdminInvoice> {
  return del(`/data/orgdashboard/${orgId}/line-items/${lineItemId}`);
}

export function fetchBillingAddresses(
  orgId: string
): Promise<Array<BillingAddress>> {
  return getJson(`/data/orgdashboard/${orgId}/billing-addresses`).then(
    // @ts-expect-error TS(2345): Argument of type '({ billingAddresses }: { billing... Remove this comment to see the full error message
    ({ billingAddresses }) => billingAddresses
  );
}

export function updateCheckoutBillingAddress(
  orgId: string,
  payload: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<void> {
  return postJson(
    `/data/orgdashboard/${orgId}/checkout/billing-address`,
    payload
  );
}

export function fetchPayments(
  orgId: string,
  filters: {
    before: string | null | undefined;
    after: string | null | undefined;
    pageIndex: number;
    perPage: number;
  }
): Promise<{
  payments: Array<ApiOrgAdminPayment>;
  count: number;
  perPage: number;
  nbPages: number;
}> {
  const { before, after, pageIndex, perPage } = filters;
  const params = {
    before,
    after,
    page: pageIndex,
    perPage,
  };
  return getJson(
    `/data/orgdashboard/${orgId}/payments?${queryString.stringify(params)}`
  );
}

export function fetchPayment(
  orgId: string,
  paymentId: string
): Promise<ApiOrgAdminPayment> {
  return getJson(`/data/orgdashboard/${orgId}/payments/${paymentId}`).then(
    // @ts-expect-error TS(2345): Argument of type '({ payment }: { payment: any; })... Remove this comment to see the full error message
    ({ payment }) => payment
  );
}

export function fetchPaymentSummary(
  orgId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<Record<string, any>> {
  return getJson(`/data/orgdashboard/${orgId}/payment-summary`).then(
    // @ts-expect-error TS(2345): Argument of type '({ report }: { report: any; }) =... Remove this comment to see the full error message
    ({ report }) => report
  );
}

export function fetchCreditPacks(
  orgId: string
): Promise<Array<ApiOrgAdminCreditPack>> {
  return getJson(`/data/orgdashboard/${orgId}/credits`).then(
    // @ts-expect-error TS(2345): Argument of type '({ packs }: { packs: any; }) => ... Remove this comment to see the full error message
    ({ packs }) => packs
  );
}

export function fetchCreditPack(
  orgId: string,
  packId: string
): Promise<{
  pack: ApiOrgAdminCreditPack;
  credits: Array<ApiOrgAdminCredit>;
}> {
  return getJson(`/data/orgdashboard/${orgId}/credits/${packId}`);
}

export function fetchCreditsForPurchase(
  orgId: string
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<Array<Record<string, any>>> {
  return getJson(`/data/orgdashboard/${orgId}/credits-for-purchase`).then(
    // @ts-expect-error TS(2345): Argument of type '({ forPurchase }: { forPurchase:... Remove this comment to see the full error message
    ({ forPurchase }) => forPurchase
  );
}

export function fetchGradProgram(
  orgId: string
): Promise<ApiOrgAdminGradProgram> {
  return getJson(`/data/orgdashboard/${orgId}/grad-program`).then(
    // @ts-expect-error TS(2345): Argument of type '({ gradProgram }: { gradProgram:... Remove this comment to see the full error message
    ({ gradProgram }) => gradProgram
  );
}

export function createGradProgram(
  orgId: string,
  programInfo: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<ApiOrgAdminGradProgram> {
  return postJson(`/data/orgdashboard/${orgId}/grad-program`, programInfo).then(
    // @ts-expect-error TS(2345): Argument of type '({ gradProgram }: { gradProgram:... Remove this comment to see the full error message
    ({ gradProgram }) => gradProgram
  );
}

export function updateGradProgram(
  orgId: string,
  programInfo: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<ApiOrgAdminGradProgram> {
  return putJson(`/data/orgdashboard/${orgId}/grad-program`, programInfo).then(
    // @ts-expect-error TS(2345): Argument of type '({ gradProgram }: { gradProgram:... Remove this comment to see the full error message
    ({ gradProgram }) => gradProgram
  );
}

export function getIntegrationJobs(
  orgId: string,
  type: ApiOrgAdminIntegrationProvider
): Promise<{
  jobs: Array<ApiOrgAdminIntegrationJob>;
  hasError: boolean;
}> {
  const url = `/data/orgdashboard/${orgId}/${type}/jobs`;
  return getJson(url);
}

export function fetchGradProgramContacts(
  orgId: string,
  // @ts-expect-error TS(2739): Type '{}' is missing the following properties from... Remove this comment to see the full error message
  {
    page,
    start,
    end,
  }: {
    page: number;
    start: string;
    end: string;
  } = {}
): Promise<{
  contacts: Array<ApiOrgAdminContact>;
  lastPage: number;
  pageIndex: number;
  count: number;
  perPage: number;
}> {
  const params = queryString.stringify(
    filterNulls({
      page,
      start,
      end,
    })
  );
  const url = `/data/orgdashboard/${orgId}/grad-program/contacts?${params}`;
  return getJson(url);
}

export function fetchGradProgramContactsCsvUrl(
  orgId: string,
  // @ts-expect-error TS(2739): Type '{}' is missing the following properties from... Remove this comment to see the full error message
  {
    start,
    end,
  }: {
    start: string;
    end: string;
  } = {}
): string {
  const params = queryString.stringify(
    filterNulls({
      start,
      end,
    })
  );
  return `/data/orgdashboard/${orgId}/grad-program/contacts.csv?${params}`;
}

export function fetchLeadsNotificationPreferences(
  orgId: string
): Promise<string> {
  const url = `/data/orgdashboard/${orgId}/grad-program/notification-preferences`;
  // @ts-expect-error TS(2345): Argument of type '({ frequency }: { frequency: any... Remove this comment to see the full error message
  return getJson(url).then(({ frequency }) => frequency);
}

export function updateLeadsNotificationPreferences(
  orgId: string,
  emailNotificationPreferencesInfo: ApiOrgAdminEmailNotificationPreferencesInfo
): Promise<string> {
  return putJson(
    `/data/orgdashboard/${orgId}/grad-program/notification-preferences`,
    emailNotificationPreferencesInfo
    // @ts-expect-error TS(2345): Argument of type '({ frequency }: { frequency: any... Remove this comment to see the full error message
  ).then(({ frequency }) => frequency);
}

export function fetchNotificationPreferences(
  orgId: string
): Promise<ApiOrgAdminOrgNotificationPreferences> {
  const url = `/data/orgdashboard/${orgId}/notification-preferences`;
  return getJson<{ preferences: ApiOrgAdminOrgNotificationPreferences }>(
    url
  ).then(({ preferences }) => preferences);
}

export function updateNotificationPreferences(
  orgId: string,
  newPreferences: ApiOrgAdminOrgNotificationPreferences
): Promise<ApiOrgAdminOrgNotificationPreferences> {
  return putJson<{ preferences: ApiOrgAdminOrgNotificationPreferences }>(
    `/data/orgdashboard/${orgId}/notification-preferences`,
    newPreferences
  ).then(({ preferences }) => preferences);
}

export function hideGradProgram({ orgId }: { orgId: string }) {
  return postJson(`/data/orgdashboard/${orgId}/grad-program/hide`).then(
    // @ts-expect-error TS(2345): Argument of type '({ gradProgram }: { gradProgram:... Remove this comment to see the full error message
    ({ gradProgram }) => gradProgram
  );
}

export function unhideGradProgram({ orgId }: { orgId: string }) {
  return postJson(`/data/orgdashboard/${orgId}/grad-program/unhide`).then(
    // @ts-expect-error TS(2345): Argument of type '({ gradProgram }: { gradProgram:... Remove this comment to see the full error message
    ({ gradProgram }) => gradProgram
  );
}

export function fetchRecommendedCandidates(
  orgId: string,
  jobId: string
): Promise<Array<ApiOrgAdminCandidate>> {
  return getJson(
    `/data/orgdashboard/${orgId}/jobs/${jobId}/suggested-applicants`
    // @ts-expect-error TS(2345): Argument of type '({ suggestedApplicants }: { sugg... Remove this comment to see the full error message
  ).then(({ suggestedApplicants }) => suggestedApplicants);
}

export function fetchRecommendedCandidate(
  applicantId: string
): Promise<ApiOrgAdminCandidate> {
  return getJson(`/data/orgdashboard/suggested-applicants/${applicantId}`).then(
    // @ts-expect-error TS(2345): Argument of type '({ suggestedApplicant }: { sugge... Remove this comment to see the full error message
    ({ suggestedApplicant }) => suggestedApplicant
  );
}

export function rateRecommendedCandidate(
  applicantId: string,
  { comments, isGood }: { comments: string; isGood: boolean }
) {
  return postJson(
    `/data/orgdashboard/suggested-applicants/${applicantId}/rate`,
    {
      applicantId,
      comments,
      isGood,
    }
  );
}

export function rateRecommendedCandidateEmail(
  applicantId: string,
  ownerId: string,
  isGood: boolean
) {
  return postJson(
    `/data/userdashboard/suggested-applicant-email/${applicantId}/rate/${ownerId}`,
    {
      isGood,
    }
  );
}

type ApiOrgAdminContactRecommendedCandidateRequestBody = {
  subject: string;
  body: string;
  fromName: string;
  replyTo: string;
  replyToOptional: string | null;
  attachJobListing: boolean;
  includeLogo: boolean;
  orgName: string;
};

export function contactRecommendedCandidate(
  applicantId: string,
  {
    subject,
    body,
    fromName,
    replyTo,
    replyToOptional,
    attachJobListing,
    includeLogo,
    orgName,
  }: ApiOrgAdminContactRecommendedCandidateRequestBody
) {
  return postJson<void>(
    `/data/orgdashboard/suggested-applicants/${applicantId}/contact`,
    {
      attachJobListing,
      body,
      fromName,
      replyTo,
      replyToOptional,
      subject,
      includeLogo,
      orgName,
    }
  );
}

export function fetchLatestAtsUsage(
  orgId: string
): Promise<ApiOrgAdminLatestAtsUsage> {
  return getJson(`/data/orgdashboard/${orgId}/latest-listings-use-ats`).then(
    // @ts-expect-error TS(2345): Argument of type '({ latestAtsUsage }: { latestAts... Remove this comment to see the full error message
    ({ latestAtsUsage }: { latestAtsUsage: ApiOrgAdminLatestAtsUsage }) =>
      latestAtsUsage
  );
}
